import * as React from 'react';
import classNames from 'classnames';


type Orientation = 'vertical' | 'horizontal';


type FlexContainerProps = {
	orientation: Orientation,
} & React.HTMLProps<HTMLDivElement>;


function FlexContainer({ orientation, children, ...otherProps }: FlexContainerProps): React.ReactNode {

	const otherClassname = otherProps.className;

	const divProps: React.HTMLProps<HTMLDivElement> = {
		...otherProps,
		className: classNames(`flex-container ${orientation}`, otherClassname) || undefined,
	};

	const newChildren = React.Children.map(children, (el: any) => {
		if (!el)
			return el;

		const isValid = el.type && el.type[SYMBOL_FLEX_ITEM];
		if (!isValid) {
			const invalidItemType = el.type ? el.type : typeof el;
			throw Error(`All FlexContainer children must be FlexItems. Invalid item of type ${invalidItemType}`);
		}

		return React.cloneElement(el, { ...el.props, orientation });
	});

	return <div {...divProps}>{newChildren}</div>;
}

function FlexHorizontal(props: Omit<FlexContainerProps, 'orientation'>): React.ReactNode {
	return <FlexContainer {...props} orientation='horizontal' />;
}

function FlexVertical(props: Omit<FlexContainerProps, 'orientation'>): React.ReactNode {
	return <FlexContainer {...props} orientation='vertical' />;
}

// Doy un par de vueltas para conseguir que todos los hijos de los FlexContainer sean FlexItems
// Creo un tipo especial que es la función FlexItem con una propiedad adicional basada en un símbolo
// privado al módulo para que no se pueda instanciar desde fuera.

const SYMBOL_FLEX_ITEM = Symbol('FlexItem');
type FlexItemProps = Omit<React.HTMLProps<HTMLDivElement>, 'size'> & { size?: string, orientation?: Orientation };
type FlexItemType = ((props: FlexItemProps) => React.ReactNode) & { [SYMBOL_FLEX_ITEM]: 'my-secret-symbol' };

function getFlexItemStyle(size?: string, orientation?: Orientation): React.CSSProperties {

	function sizeIsStar(width: string): boolean {
		return /\d*\*{1}/.test(width);
	}

	function sizeIsNumber(width: string): boolean {
		return /^[\d]*$/.test(width);
	}

	function formatSize(size?: string) {
		if (!size)
			return undefined;

		return sizeIsNumber(size) ? parseInt(size) : size;
	}

	if (size !== undefined && (size === '*' || sizeIsStar(size))) {
		return {
			flexGrow: size === '*' ? 1 : parseInt(size.slice(0, -1)),
			flexBasis: 0,
			overflow: 'auto'
		};
	}

	return {
		// Evito que me espachurren
		flexShrink: 0,
		width: orientation && orientation === 'horizontal' ? formatSize(size) : 'auto',
		height: orientation && orientation === 'vertical' ? formatSize(size) : 'auto',
	};
}

const FlexItem: FlexItemType = function ({ size, orientation, ...otherProps }: FlexItemProps): React.ReactNode {

	// Evito renderizar nada si todos los children son null. De esta forma podemos meter alegremente
	// Flex.Item por donde queramos para que el código se vea uniforme, sin sobrecargar el
	// DOM con divs inútiles cuando el contenido se muesta de forma opcional, por ejemplo en casos
	// como <Flex.Item>{hasDiscount ? <span>Descuento:</span> : null}</Flex.Item>

	const allChildrenAreNull = React.Children.toArray(otherProps.children)
		.every(x => x === null);

	if (allChildrenAreNull)
		return null;

	// la clase flex-item de momento sólo la uso para facilitar la lectura del html
	let className = 'flex-item';
	if (otherProps.className)
		className += ' ' + otherProps.className;

	const divProps = {
		...otherProps,
		className: className,
		style: {
			...getFlexItemStyle(size, orientation),
			...otherProps.style
		}
	};
	return <div {...divProps} />;
} as FlexItemType;

FlexItem[SYMBOL_FLEX_ITEM] = 'my-secret-symbol';


export const Flex = {
	Horizontal: FlexHorizontal,
	Vertical: FlexVertical,
	Item: FlexItem
};
